import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CampaignIcon from '@mui/icons-material/Campaign';
import { toLower } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';

import LinearProgress from '../../../LinearProgress';
import { daysLeft } from '../../../../../Utils/Steps';
import {
  ConsultationProgressStatuses,
  getConsultationProgressStatus,
  stepName,
  totalDays,
  inNDays,
} from './utils';

const itsImplementation = (intl: any) => (
  <Typography
    variant="body2"
    color="textSecondary"
    sx={(theme: any) => ({
      fontWeight: theme.typography.fontWeightMedium,
      display: 'inline-flex',
      alignItems: 'center',
    })}
  >
    <FormattedMessage id="POST_STEPS_TYPES.DEFAULT.IMPLEMENTATION" />{' '}
    {toLower(intl.formatMessage({ id: 'POSTS.STEPS.STATES.AVAILABLE' }))}
    <CampaignIcon sx={{ marginLeft: 0.5 }} color="secondary" />
  </Typography>
);

const itsOngoing = (currentStep: any, postType: any) => {
  const intl = useIntl();

  const datesStatus = intl
    .formatMessage({ id: `POSTS.STEPS.STATES.${currentStep.dates_status.toUpperCase()}` })
    .toLowerCase();

  return (
    <Box
      sx={(theme: any) => ({
        width: '90%',

        [theme.breakpoints.down('sm')]: {
          width: '100%',
        },
      })}
    >
      <Box
        sx={{
          mb: 1,
          gap: 1,
          display: 'flex',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 'medium' }} color="textSecondary">
          {stepName(currentStep, postType)} {datesStatus}.
        </Typography>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }} color="textSecondary">
            {currentStep.endless ? (
              <FormattedMessage id="POSTS.STEPS.STATES.ENDLESS" />
            ) : (
              <FormattedMessage
                id="DATE_TIME.DAYS_LEFT"
                values={{ count: daysLeft(currentStep) }}
              />
            )}
          </Typography>
        </Box>
      </Box>

      <LinearProgress
        value={100 - (daysLeft(currentStep) * 100) / totalDays(currentStep)}
        title={intl.formatMessage({ id: 'DATE_TIME.DAYS_LEFT' }, { count: daysLeft(currentStep) })}
      />
    </Box>
  );
};

const itsComing = (currentStep: any, post: any, intl: any) => {
  const comingText = () =>
    intl.formatMessage({ id: 'DATE_TIME.IN_N_DAYS' }, { count: inNDays(currentStep) });

  return (
    <Typography variant="body2" sx={{ fontWeight: 'medium' }} color="textSecondary">
      {stepName(currentStep, post.type)}
      {` : ${toLower(comingText())}`}
    </Typography>
  );
};

const itsEnded = (
  <Typography
    variant="body2"
    color="textSecondary"
    sx={(theme: any) => ({
      fontWeight: theme.typography.fontWeightMedium,
      display: 'inline-flex',
      alignItems: 'center',
    })}
  >
    <FormattedMessage id="POSTS.STEPS.STATES.ENDED" />
    <CheckCircleOutlineIcon sx={{ marginLeft: 0.5 }} color="secondary" />
  </Typography>
);

function ConsultationProgress({ post }: any) {
  const intl = useIntl();
  const {
    current_step_id: currentStepId,
    included: { steps },
  } = post;
  const currentStep = steps.find((step: any) => step.id === currentStepId);
  const progressStatus = getConsultationProgressStatus(currentStep);

  switch (progressStatus) {
    case ConsultationProgressStatuses.ENDED:
      return itsEnded;
    case ConsultationProgressStatuses.IMPLEMENTATION:
      return itsImplementation(intl);
    case ConsultationProgressStatuses.ONGOING:
      return itsOngoing(currentStep, post.type);
    case ConsultationProgressStatuses.COMING:
      return itsComing(currentStep, post, intl);
    default:
      break;
  }
}

export default ConsultationProgress;
